import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { ForwardedRef, forwardRef, useEffect, useState } from 'react'
import styled from 'styled-components'

import Button from 'shared/components/Button'
import { colors, fonts, media } from 'shared/lib'
import { CloseX } from 'shared/components/Icons/SVGIcons/CloseX'
import { FavoritesSizePickerOption } from 'favorites/types'
import { getSizeLabel } from 'productTemplate/utils/getSizeLabel'
import { SizePicker } from 'favorites/SizePicker'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { t } from 'localization'
import { useFetchUserFavoritesForPTQuery } from 'api/productTemplateListsApi'
import { useSaveUserFavoritesMutation } from 'api/productTemplateListsApi'
import { useUser } from 'shared/hooks/useUser'

type FavoritesModalContentsProps = {
  isApparel: boolean
  sizeCategory: string
  handleClose: () => void
  productTemplateId: string
}

export const FavoritesModalContents = forwardRef(
  (
    { isApparel, sizeCategory, handleClose, productTemplateId }: FavoritesModalContentsProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { isAuthenticated } = useUser()
    const { data: userFavorites } = useFetchUserFavoritesForPTQuery(
      {
        productTemplateId,
      },
      { skip: !isAuthenticated },
    )
    const [saveUserFavorites, { isLoading }] = useSaveUserFavoritesMutation()

    const [selectedSizes, setSizes] = useState<number[]>(userFavorites?.sizes || [])

    const TP = 'favorites.components.FavoritesModalContents'

    const sizeLabel = getSizeLabel({
      isApparel,
      sizeCategory,
      TP: {
        basePath: TP,
        youthLabelPath: 'sizeLabelYouth',
        defaultLabelPath: 'sizeLabel',
        fallbackLabel: `US {sizeCategory} Sizes`,
      },
    })

    const hasFavorites =
      isAuthenticated && userFavorites?.sizes ? userFavorites?.sizes?.length > 0 : false

    const handleSizeSelections = (option: FavoritesSizePickerOption) => {
      const sizeAlreadySelected = selectedSizes.find(
        (selectionValue) => option.size === selectionValue,
      )
      if (sizeAlreadySelected) {
        setSizes((oldArr) =>
          oldArr.filter((selectionValue) => {
            return selectionValue !== option.size
          }),
        )
      } else {
        setSizes((oldArr) => [...oldArr, option.size])
      }
    }

    const handleSendTrackingEvent = () => {
      const currentlySelected = new Set(selectedSizes)
      const unselectedSizes =
        userFavorites?.sizes?.reduce((arr, size) => {
          if (!currentlySelected.has(size)) {
            arr.push(size)
          }
          return arr
        }, [] as number[]) || []

      sendTrackingEvent('FAVORITE_SAVE_TAP', {
        product_template_slug: userFavorites?.productTemplate?.slug || '',
        sizes_selected: `[${selectedSizes}]`,
        sizes_unselected: `[${unselectedSizes}]`,
      })
    }

    const handleSave = async () => {
      try {
        handleSendTrackingEvent()
        await saveUserFavorites({ productTemplateId, sizes: selectedSizes })
        handleClose()
      } catch (error) {
        Bugsnag.notify(error as NotifiableError)
      }
    }

    useEffect(() => {
      if (userFavorites?.sizes) setSizes(userFavorites.sizes)
    }, [userFavorites?.sizes])

    return (
      <FavoritesModalContentsWrapper ref={ref}>
        <ModalHeader>
          <ModalLabel data-qa="FavoritesModalLabel">
            {t(`${TP}.modalLabel`, 'Save Favorite')}
          </ModalLabel>
          <CloseButton data-qa="FavoritesModalCloseButton" onClick={handleClose}>
            <CloseX />
          </CloseButton>
        </ModalHeader>
        <ModalDescription data-qa="FavoritesModalDescription">
          {t(
            `${TP}.modalDescription`,
            'Receive exclusive updates on price changes, restocks and more when you add items to your Favorites.',
          )}
        </ModalDescription>
        <SizingLabel data-qa="FavoritesModalSizeLabel">{sizeLabel}</SizingLabel>
        <SizePicker
          onChange={handleSizeSelections}
          selectedSizes={selectedSizes}
          sizePickerOptions={userFavorites?.productTemplate?.sizes || []}
        />
        <ButtonWrapper>
          <Button
            buttonType="primary2"
            disabled={isLoading || (!hasFavorites && selectedSizes?.length === 0)}
            $fill
            onClick={handleSave}
            qaAttr="FavoritesModalSaveButton"
            data-qa="FavoritesModalSaveButton"
          >
            {t(`${TP}.modalSaveButtonText`, 'Save')}
          </Button>
        </ButtonWrapper>
      </FavoritesModalContentsWrapper>
    )
  },
)

FavoritesModalContents.displayName = 'FavoritesModalContents'

const FavoritesModalContentsWrapper = styled.div`
  background-color: ${colors.FC2_WHITE};
  padding: 20px;

  ${media.large`
  max-width: 450px;
  border: 1px solid ${colors.FC2_BLACK}
`}
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

const CloseButton = styled.button`
  width: 15px;
  height: 15px;
`

const ModalLabel = styled.div`
  margin-bottom: 20px;
  text-transform: uppercase;
  ${fonts.SUBTITLE_1}
`

const ModalDescription = styled.div`
  margin-bottom: 40px;
  font-size: 14px;
  color: ${colors.FC2_BLACK};
`

const SizingLabel = styled.div`
  margin-bottom: 20px;
  ${fonts.SUBTITLE_2}
`

const ButtonWrapper = styled.div`
  margin-top: 40px;
  > button {
    text-transform: uppercase;
  }
`
